import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { WaapConsoleBreadcrumbs } from '../WaapConsoleBreadcrumbs';
import { WaapEmptyCard } from '../WaapEmptyCard';
import { useWaapWebsite } from '../waap-context';
import { WebAccessControlSettings } from './WebAccessControlSettings';
import { VerifiedBotSettings } from './VerifiedBotSettings';
import { ResponseFilteringSettings } from './ResponseFilteringSettings';
import { HttpSettings } from './HttpSettings';
import { MonthlySecurityReportSettings } from './MonthlySecurityReportSettings';
import { PackageSettings } from './PackageSettings';
import { WaapDangerZone } from './WaapDangerZone';

const TABS = [
  { slug: 'subscription', label: 'Subscription' },
  { slug: 'web-access-control', label: 'Web Access Control' },
  { slug: 'verified-bot', label: 'Verified Bot' },
  { slug: 'response-filtering', label: 'Response Filtering' },
  { slug: 'http-settings', label: 'HTTP Settings' },
  { slug: 'monthly-security-report', label: 'Monthly Security Report' },
  { slug: 'danger', label: 'Danger zone' },
] as const;

/**
 * WAAP Settings — account-level settings page. Five tabs mirror the portal:
 * Web Access Control (IP blacklist), Verified Bot, Response Filtering, HTTP
 * Settings and Monthly Security Report. The first four are backed by the account
 * global-config; the last by /msiEmail. All are account-wide, so a representative
 * website from the WAAP context resolves the customer (there is no per-website
 * picker), matching the Emergency Mitigation / strategy pages.
 */
export function WaapSettingsPage() {
  const [tab, setTab] = useState<string>(TABS[0].slug);
  const { serviceId } = useParams<{ serviceId: string }>();
  const { selected, isLoading: loadingSites } = useWaapWebsite();
  const domainId = selected?.domainId;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Settings' }]} />}
        title="Settings"
      />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Subscription is service-scoped (the plan lives on the WAAP customer
          entity, not a website) — render it without needing a website. */}
      {tab === 'subscription' ? (
        <PackageSettings serviceId={serviceId ?? ''} />
      ) : tab === 'danger' ? (
        <WaapDangerZone serviceId={serviceId ?? ''} />
      ) : !domainId ? (
        <WaapEmptyCard
          icon={<Settings size={20} />}
          title={loadingSites ? 'Loading…' : 'No protected websites'}
          description={
            loadingSites
              ? 'Resolving your WAAP websites.'
              : 'Add a WAAP website before configuring account settings.'
          }
        />
      ) : tab === 'web-access-control' ? (
        <WebAccessControlSettings domainId={domainId} />
      ) : tab === 'verified-bot' ? (
        <VerifiedBotSettings domainId={domainId} />
      ) : tab === 'response-filtering' ? (
        <ResponseFilteringSettings domainId={domainId} />
      ) : tab === 'http-settings' ? (
        <HttpSettings domainId={domainId} />
      ) : (
        <MonthlySecurityReportSettings domainId={domainId} />
      )}
    </Container>
  );
}
